/**
 * AgentChat Component
 * Full-screen chat interface for AI Agent interactions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import agentChatService from '../services/agentChatService';
import {
  sendMcpApproval,
  createUserMessage,
  createThinkingMessage,
  createErrorMessage,
  createAssistantMessage,
  createConfirmationMessage,
  createToolExecutionMessage,
} from '../services/agentChatService';
import './AgentChat.css';

// Constants
const STORAGE_KEY = 'inpods-agent-chat';
const MAX_PERSISTED_MESSAGES = 50;

// Markdown-like simple parser for basic formatting
const formatMessageContent = (content) => {
  // Split content by code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return (
    <>
      {parts.map((part, index) => {
        // Handle code blocks
        if (part.startsWith('```') && part.endsWith('```')) {
          const codeContent = part.slice(3, -3);
          const lines = codeContent.split('\n');
          const language = lines[0]?.trim() || '';
          const code = language ? lines.slice(1).join('\n') : codeContent;

          return (
            <pre key={index} className="code-block" data-language={language}>
              <code>{code}</code>
            </pre>
          );
        }

        // Handle inline formatting
        return (
          <span key={index}>
            {part.split('\n').map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {formatInlineText(line)}
              </React.Fragment>
            ))}
          </span>
        );
      })}
    </>
  );
};

// Format inline text (bold, italic, code)
const formatInlineText = (text) => {
  // Handle inline code
  const parts = text.split(/(`[^`]+`)/g);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={index} className="inline-code">{part.slice(1, -1)}</code>;
        }
        
        // Handle bold
        const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
        return (
          <React.Fragment key={index}>
            {boldParts.map((boldPart, boldIndex) => {
              if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                return <strong key={boldIndex}>{boldPart.slice(2, -2)}</strong>;
              }
              
              // Handle italic
              const italicParts = boldPart.split(/(\*[^*]+\*)/g);
              return (
                <React.Fragment key={boldIndex}>
                  {italicParts.map((italicPart, italicIndex) => {
                    if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
                      return <em key={italicIndex}>{italicPart.slice(1, -1)}</em>;
                    }
                    return italicPart;
                  })}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

// Format timestamp
const formatTime = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

// Tool status icon
const getToolStatusIcon = (status) => {
  switch (status) {
    case 'pending': return '⏳';
    case 'executing': return '🔄';
    case 'success': return '✅';
    case 'error': return '❌';
    default: return '🔧';
  }
};

// Message component
const MessageItem = ({
  message,
  onCopy,
  onConfirm,
  onEdit,
  isPendingConfirmation,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStartEdit = () => {
    setEditedContent(message.confirmationData?.originalInstruction || message.content);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (onEdit && editedContent.trim()) {
      onEdit(editedContent.trim());
      setIsEditing(false);
    }
  };

  const roleClasses = {
    user: 'message-user',
    assistant: 'message-assistant',
    system: 'message-system',
    thinking: 'message-thinking',
    confirmation: 'message-confirmation',
    tool_execution: 'message-tool',
    error: 'message-error',
  };

  const roleIcons = {
    user: '👤',
    assistant: '🤖',
    system: 'ℹ️',
    thinking: '💭',
    confirmation: '❓',
    tool_execution: '🔧',
    error: '⚠️',
  };

  return (
    <div className={`message ${roleClasses[message.role] || ''}`}>
      <div className="message-avatar">
        <span>{roleIcons[message.role] || '💬'}</span>
      </div>
      
      <div className="message-content-wrapper">
        <div className="message-content">
          {message.role === 'thinking' ? (
            <div className="thinking-indicator">
              <span className="thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
              <span className="thinking-text">{message.content}</span>
            </div>
          ) : message.role === 'tool_execution' && message.toolCalls ? (
            <div className="tool-execution">
              {message.toolCalls.map((tool) => (
                <div key={tool.id} className={`tool-item tool-${tool.status}`}>
                  <span className="tool-icon">{getToolStatusIcon(tool.status)}</span>
                  <span className="tool-name">{tool.name}</span>
                  <span className="tool-status">{tool.status}</span>
                  {tool.result && (
                    <div className="tool-result">
                      <pre>{typeof tool.result === 'string' ? tool.result : JSON.stringify(tool.result, null, 2)}</pre>
                    </div>
                  )}
                  {tool.error && (
                    <div className="tool-error">{tool.error}</div>
                  )}
                </div>
              ))}
            </div>
          ) : message.role === 'confirmation' ? (
            <div className="confirmation-content">
              <div className="message-text">
                {formatMessageContent(message.content)}
              </div>
              {/* Show MCP approval details if available */}
              {message.confirmationData && (message.confirmationData.toolName || message.confirmationData.id) && (
                <div className="mcp-approval-details">
                  {message.confirmationData.toolName && (
                    <div className="mcp-detail">
                      <span className="mcp-label">Tool:</span> 
                      <span className="mcp-value">{message.confirmationData.toolName}</span>
                    </div>
                  )}
                  {message.confirmationData.serverLabel && (
                    <div className="mcp-detail">
                      <span className="mcp-label">Server:</span> 
                      <span className="mcp-value">{message.confirmationData.serverLabel}</span>
                    </div>
                  )}
                  {message.confirmationData.arguments && (
                    <details className="mcp-arguments">
                      <summary>View arguments</summary>
                      <pre>{(() => {
                        try {
                          const args = typeof message.confirmationData.arguments === 'string' 
                            ? JSON.parse(message.confirmationData.arguments) 
                            : message.confirmationData.arguments;
                          return JSON.stringify(args, null, 2);
                        } catch {
                          return message.confirmationData.arguments;
                        }
                      })()}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="message-text">
              {formatMessageContent(message.content)}
            </div>
          )}

          {/* Confirmation buttons */}
          {message.role === 'confirmation' && message.needsConfirmation && isPendingConfirmation && (
            <div className="confirmation-actions">
              {isEditing ? (
                <div className="edit-instruction">
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="edit-textarea"
                    rows={4}
                  />
                  <div className="edit-buttons">
                    <button 
                      className="btn-save"
                      onClick={handleSaveEdit}
                    >
                      Save & Execute
                    </button>
                    <button 
                      className="btn-cancel"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="confirmation-buttons">
                  {/* Check if this is an MCP approval request (has id and responseId) */}
                  {message.confirmationData?.id && message.confirmationData?.responseId ? (
                    // MCP Approval with modes
                    <>
                      <button 
                        className="btn-reject"
                        onClick={() => onConfirm?.(false, 'once')}
                      >
                        ✗ Deny
                      </button>
                      <button 
                        className="btn-approve-once"
                        onClick={() => onConfirm?.(true, 'once')}
                      >
                        ✓ Approve Once
                      </button>
                      <button 
                        className="btn-approve-tool"
                        onClick={() => onConfirm?.(true, 'always_this_tool')}
                      >
                        ✓✓ Always This Tool
                      </button>
                      <button 
                        className="btn-approve-all"
                        onClick={() => onConfirm?.(true, 'always_all')}
                      >
                        ✓✓✓ Always All
                      </button>
                    </>
                  ) : (
                    // Legacy confirmation
                    <>
                      <button 
                        className="btn-confirm"
                        onClick={() => onConfirm?.(true, 'once')}
                      >
                        ✓ Approve
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => onConfirm?.(false, 'once')}
                      >
                        ✗ Reject
                      </button>
                      {message.confirmationData?.originalInstruction && (
                        <button 
                          className="btn-edit"
                          onClick={handleStartEdit}
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="message-meta">
          <span className="message-time">{formatTime(new Date(message.timestamp))}</span>
          <button 
            className="btn-copy" 
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            {copied ? '✓' : '📋'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main AgentChat component
const AgentChat = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [chatInputEnabled, setChatInputEnabled] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Load persisted conversation on mount
  useEffect(() => {
    const loadPersistedConversation = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          const loadedMessages = data.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }));
          setMessages(loadedMessages);
          setConversationId(data.conversationId);
        }
      } catch (err) {
        console.error('Failed to load persisted conversation:', err);
      }
    };

    loadPersistedConversation();
  }, []);

  // Persist conversation on messages change
  useEffect(() => {
    const persistConversation = () => {
      if (messages.length === 0) return;

      try {
        const toStore = {
          conversationId: conversationId || '',
          messages: messages.slice(-MAX_PERSISTED_MESSAGES).map(msg => ({
            ...msg,
            timestamp: msg.timestamp.toISOString(),
          })),
          lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      } catch (err) {
        console.error('Failed to persist conversation:', err);
      }
    };

    persistConversation();
  }, [messages, conversationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle authentication redirect
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Add message helper
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  // Remove last message helper
  const removeLastMessage = useCallback(() => {
    setMessages(prev => prev.slice(0, -1));
  }, []);

  // Handle stream chunks
  const handleStreamChunk = useCallback((chunk) => {
    console.log('Processing chunk in component:', chunk);
    
    // Safely extract data with fallbacks
    const chunkData = chunk?.data || chunk || {};
    const chunkType = chunk?.type || 'content';
    const content = chunkData?.content || chunkData?.message || chunkData?.text || '';
    
    switch (chunkType) {
      case 'conversation_start':
      case 'conversation_id':
        // Set conversation ID when stream starts or updates
        const convId = chunkData?.conversation_id || chunkData?.conversationId;
        if (convId) {
          setConversationId(convId);
        }
        break;
      
      case 'status':
        // Log status updates (don't show in UI)
        console.log('Status update:', chunkData?.status);
        break;
      
      case 'done':
        // Stream completed - don't add duplicate message
        // The full_message is already accumulated in content chunks
        console.log('Stream complete');
        break;
      
      case 'unknown':
        // Ignore unknown event types
        break;
        
      case 'thinking':
        // Update or add thinking message
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.role === 'thinking') {
            return [
              ...prev.slice(0, -1),
              { ...lastMsg, content: content || 'Thinking...' },
            ];
          }
          return [...prev, createThinkingMessage(content || 'Thinking...')];
        });
        break;

      case 'content':
      case 'message':
      case 'text':
        // Update or add assistant message
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.role === 'thinking') {
            // Replace thinking with content
            return [
              ...prev.slice(0, -1),
              createAssistantMessage(content),
            ];
          } else if (lastMsg?.role === 'assistant') {
            // Append to existing assistant message
            return [
              ...prev.slice(0, -1),
              { ...lastMsg, content: lastMsg.content + content },
            ];
          }
          return [...prev, createAssistantMessage(content)];
        });
        break;

      case 'tool_call':
      case 'tool':
      case 'function_call':
        addMessage(createToolExecutionMessage(
          chunkData?.name || chunkData?.tool_name || 'tool',
          chunkData?.status || 'executing',
          chunkData?.result,
          chunkData?.error
        ));
        break;

      case 'confirmation':
        setPendingConfirmation(chunkData);
        addMessage(createConfirmationMessage(
          chunkData?.message || 'Please confirm this action.',
          chunkData
        ));
        break;
        
      case 'mcpApprovalRequest':
      case 'mcp_approval_request':
        // MCP tool approval request (backend sends approval_request nested object)
        const approvalRequest = chunkData?.approval_request || chunkData;
        const approvalData = {
          id: approvalRequest?.id,
          toolName: approvalRequest?.tool_name,
          serverLabel: approvalRequest?.server_label,
          arguments: approvalRequest?.arguments,
          responseId: approvalRequest?.response_id, // Backend uses response_id not previous_response_id
          message: `Tool "${approvalRequest?.tool_name || 'Unknown'}" from ${approvalRequest?.server_label || 'MCP Server'} requires your approval.`
        };
        
        // Disable chat input during approval
        setChatInputEnabled(false);
        setPendingConfirmation(approvalData);
        
        // Remove thinking message if present
        setMessages(prev => {
          const filtered = prev.filter(m => m.role !== 'thinking');
          return [...filtered, createConfirmationMessage(approvalData.message, approvalData)];
        });
        break;

      case 'error':
        const errorMsg = chunkData?.message || chunkData?.error || content || 'An error occurred';
        setMessages(prev => {
          const filtered = prev.filter(m => m.role !== 'thinking');
          return [...filtered, createErrorMessage(errorMsg)];
        });
        break;

      default:
        // Ignore unhandled event types to prevent raw JSON display
        console.log('Unhandled chunk type:', chunkType, chunk);
        break;
    }
  }, [addMessage]);

  // Handle stream complete
  const handleStreamComplete = useCallback((response) => {
    console.log('Stream complete:', response);
    
    // Safely extract conversation ID from various formats
    const convId = response?.conversation_id || response?.conversationId || response?.id;
    if (convId) {
      setConversationId(convId);
    }
    
    setIsLoading(false);
    
    // Remove any remaining thinking messages
    setMessages(prev => prev.filter(m => m.role !== 'thinking'));

    // Check for pending confirmation in various formats
    const needsConfirm = response?.needs_confirmation || response?.needsConfirmation || 
                         response?.requires_confirmation || response?.pending_confirmation ||
                         response?.awaiting_confirmation;
    const confirmData = response?.confirmation_data || response?.confirmationData || response?.confirmation;
    
    if (needsConfirm) {
      const confirmationInfo = confirmData || {
        message: response?.full_message || response?.content || response?.message || 'Please confirm this action.',
        action: response?.action || response?.tool_name,
        details: response?.details || {}
      };
      setPendingConfirmation(confirmationInfo);
      
      // Add a confirmation message to the chat
      const confirmMessage = confirmationInfo.message || response?.full_message || 'Please confirm this action.';
      if (confirmMessage) {
        addMessage(createConfirmationMessage(confirmMessage, confirmationInfo));
      }
      return;
    }
    
    // If response contains a final message (full_message or content), add it
    const finalContent = response?.full_message || response?.content || response?.message || response?.text;
    if (finalContent && typeof finalContent === 'string' && finalContent.trim()) {
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        // Only add if it's different from the last message
        if (lastMsg?.role !== 'assistant' || lastMsg?.content !== finalContent) {
          return [...prev, createAssistantMessage(finalContent)];
        }
        return prev;
      });
    }
  }, [addMessage]);

  // Handle stream error
  const handleStreamError = useCallback((errorMessage) => {
    setIsLoading(false);
    setError(errorMessage);
    
    // Remove thinking message and add error
    setMessages(prev => {
      const filtered = prev.filter(m => m.role !== 'thinking');
      return [...filtered, createErrorMessage(errorMessage)];
    });
  }, []);

  // Send message
  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Clear input and errors
    setInputValue('');
    setError(null);
    setPendingConfirmation(null);

    // Add user message
    const userMessage = createUserMessage(trimmedInput);
    addMessage(userMessage);

    // Add thinking indicator
    addMessage(createThinkingMessage());
    setIsLoading(true);

    try {
      // Try streaming first, fallback to non-streaming
      const abort = await agentChatService.sendMessageStream(
        trimmedInput,
        conversationId,
        handleStreamChunk,
        handleStreamError,
        handleStreamComplete
      );
      
      abortControllerRef.current = abort;
    } catch (err) {
      // Fallback to non-streaming API
      try {
        const response = await agentChatService.sendMessage(trimmedInput, conversationId || undefined);
        
        // Remove thinking message
        removeLastMessage();
        
        // Process response
        setConversationId(response.conversation_id);
        
        if (response.tool_calls && response.tool_calls.length > 0) {
          response.tool_calls.forEach(tool => {
            addMessage(createToolExecutionMessage(tool.name, tool.status, tool.result, tool.error));
          });
        }

        if (response.needs_confirmation && response.confirmation_data) {
          setPendingConfirmation(response.confirmation_data);
          addMessage(createConfirmationMessage(response.content, response.confirmation_data));
        } else {
          addMessage(createAssistantMessage(response.content, response.tool_calls));
        }
        
        setIsLoading(false);
      } catch (fallbackErr) {
        handleStreamError(fallbackErr.message || 'Failed to send message');
      }
    }
  };

  // Handle confirmation
  const handleConfirmation = async (confirmed, approvalMode = 'once') => {
    if (!conversationId || !pendingConfirmation) return;

    const approvalData = pendingConfirmation;
    setPendingConfirmation(null);
    setIsLoading(true);

    // Check if this is an MCP approval (has id and responseId fields)
    const isMcpApproval = approvalData.id && approvalData.responseId;

    if (isMcpApproval) {
      // Use MCP approval with streaming (same endpoint, different payload)
      try {
        const abort = await sendMcpApproval(
          approvalData.id,
          confirmed,
          approvalData.responseId,
          conversationId,
          approvalMode, // "once", "always_this_tool", or "always_all"
          handleStreamChunk,
          handleStreamError,
          (response) => {
            handleStreamComplete(response);
            // Re-enable chat input after approval completes
            setChatInputEnabled(true);
          }
        );
        
        abortControllerRef.current = abort;
      } catch (err) {
        handleStreamError(err.message || 'Failed to process MCP approval');
        setChatInputEnabled(true);
      }
    } else {
      // Use legacy confirmation endpoint
      addMessage(createThinkingMessage(confirmed ? 'Executing...' : 'Cancelling...'));

      try {
        const response = await agentChatService.confirmAction(
          conversationId,
          confirmed,
          approvalData
        );

        removeLastMessage(); // Remove thinking

        if (response.tool_calls && response.tool_calls.length > 0) {
          response.tool_calls.forEach(tool => {
            addMessage(createToolExecutionMessage(tool.name, tool.status, tool.result, tool.error));
          });
        }

        if (response.needs_confirmation && response.confirmation_data) {
          setPendingConfirmation(response.confirmation_data);
          addMessage(createConfirmationMessage(response.content, response.confirmation_data));
        } else {
          addMessage(createAssistantMessage(response.content));
        }
      } catch (err) {
        handleStreamError(err.message || 'Failed to process confirmation');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle edit instruction
  const handleEditInstruction = async (editedInstruction) => {
    if (!conversationId) return;

    setPendingConfirmation(null);
    setIsLoading(true);
    addMessage(createThinkingMessage('Processing edited instruction...'));

    try {
      const response = await agentChatService.editInstruction(
        conversationId,
        editedInstruction
      );

      removeLastMessage();

      if (response.needs_confirmation && response.confirmation_data) {
        setPendingConfirmation(response.confirmation_data);
        addMessage(createConfirmationMessage(response.content, response.confirmation_data));
      } else {
        addMessage(createAssistantMessage(response.content));
      }
    } catch (err) {
      handleStreamError(err.message || 'Failed to edit instruction');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopy = (content) => {
    navigator.clipboard.writeText(content).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Clear conversation
  const handleClearConversation = async () => {
    if (conversationId) {
      await agentChatService.clearConversation(conversationId);
    }
    
    setMessages([]);
    setConversationId(null);
    setPendingConfirmation(null);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Export conversation as markdown
  const handleExportMarkdown = () => {
    const markdown = messages.map(msg => {
      const time = formatTime(new Date(msg.timestamp));
      const role = msg.role.charAt(0).toUpperCase() + msg.role.slice(1);
      return `### ${role} (${time})\n\n${msg.content}\n`;
    }).join('\n---\n\n');

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Handle input key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle retry
  const handleRetry = () => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      if (lastUserMessage) {
        // Remove error message
        setMessages(prev => prev.filter(m => m.role !== 'error'));
        setError(null);
        setInputValue(lastUserMessage.content);
      }
    }
  };

  return (
    <div className="agent-chat">
      {/* Header */}
      <header className="agent-chat-header">
        <div className="header-left">
          <h1>🤖 AI Course Assistant</h1>
          <span className="header-subtitle">Create and manage courses with AI</span>
        </div>
        <div className="header-right">
          <div className="export-menu-container">
            <button 
              className="btn-header"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              📥 Export
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={handleExportMarkdown}>
                  📄 Export as Markdown
                </button>
              </div>
            )}
          </div>
          <button 
            className="btn-header btn-clear"
            onClick={handleClearConversation}
          >
            🗑️ Clear
          </button>
        </div>
      </header>

      {/* Messages */}
      <div className="agent-chat-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🤖</div>
            <h2>Welcome to AI Course Assistant</h2>
            <p>I can help you create courses, lessons, and manage your content. Try asking me to:</p>
            <ul className="suggestions">
              <li onClick={() => setInputValue('Create a Machine Learning course for the CS department')}>
                "Create a Machine Learning course for the CS department"
              </li>
              <li onClick={() => setInputValue('Add a new lesson about Neural Networks')}>
                "Add a new lesson about Neural Networks"
              </li>
              <li onClick={() => setInputValue('Generate quiz questions for my course')}>
                "Generate quiz questions for my course"
              </li>
            </ul>
          </div>
        ) : (
          messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              onCopy={handleCopy}
              onConfirm={handleConfirmation}
              onEdit={handleEditInstruction}
              isPendingConfirmation={!!pendingConfirmation && message.needsConfirmation === true}
            />
          ))
        )}
        
        {/* Error with retry */}
        {error && !isLoading && (
          <div className="error-banner">
            <span>{error}</span>
            <button className="btn-retry" onClick={handleRetry}>
              🔄 Retry
            </button>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="agent-chat-input">
        <div className="input-wrapper">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message... (Shift+Enter for new line)"
            disabled={isLoading || !chatInputEnabled || !!pendingConfirmation}
            rows={1}
          />
          <button
            className="btn-send"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading || !chatInputEnabled || !!pendingConfirmation}
          >
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : (
              '➤'
            )}
          </button>
        </div>
        {pendingConfirmation && (
          <div className="pending-notice">
            ⏳ Waiting for approval - Please respond to the confirmation above before sending a new message.
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChat;
